/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { addToast } from "actions/ToastMessageActions";
import { AppHeaderHeight } from "components/shared/AppHeader";
import { TitledFloatingPanel } from "components/shared/FloatingPanel";
import { webComponentShim } from "components/shared/WebComponentShim";
import { useNonTextEventListener } from "hooks/useEventListener";
import { useCallback, useEffect, useState } from "react";
import * as React from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { FeatureFlagDebugSection } from "components/DebugPanel/FeatureFlagDebugSection";
import { RecordingSessionsDebugSection } from "components/DebugPanel/RecordingSessionsDebugSection";
import { ParticipantsDebugSection } from "components/DebugPanel/ParticipantsDebugSection";
import { ProjectStatsDebugSection } from "components/DebugPanel/ProjectStatsDebugSection";

const DebugFloatingPanel = styled(TitledFloatingPanel)`
  position: fixed;
  left: 24px;
  top: calc(${AppHeaderHeight} + 24px);
  bottom: 120px;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-width: 400px;
`;

const CopyStateButton = webComponentShim("sp-action-button");
const StyledCopyStateButton = styled(CopyStateButton)`
  padding: 8px;
  cursor: pointer;
  font-weight: bold;
  margin-bottom: 8px;
  border: grey 1px solid;
`;

export default function DebugPanel(): JSX.Element | null {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const keydownHandler = useCallback(
    (event) => {
      if (event.shiftKey || event.altKey || event.key !== "`") {
        return;
      }
      setIsOpen(!isOpen);
    },
    [isOpen]
  );

  useNonTextEventListener("keydown", keydownHandler);

  if (!isOpen) {
    return null;
  }

  const copyAppState = (): void => {
    dispatch((_, getState) => {
      navigator.clipboard.writeText(JSON.stringify(getState(), null, 2));
    });
    dispatch(addToast({ message: "Copied app state to clipboard." }));
  };

  return (
    <DebugFloatingPanel
      closeAltText="Close"
      onClose={(): void => setIsOpen(false)}
      title="Debug"
    >
      <div>
        <h2>Version {VERSION}</h2>
        <FeatureFlagDebugSection />
        <StyledCopyStateButton onClick={copyAppState}>
          Copy app state
        </StyledCopyStateButton>
        <ProjectStatsDebugSection />
        <RecordingSessionsDebugSection />
        <ParticipantsDebugSection />
      </div>
    </DebugFloatingPanel>
  );
}
